/**
 * Analyzer: ATM Version Comparison
 *
 * Reads the ATM template version history (managed by useAtmManagement →
 * ntTemplateVersions) and asks the question the trader can't easily ask
 * themselves: "did my last bracket change actually help?"
 *
 * Without this analyzer, version-related bracket tweaks live in a vacuum —
 * the trader changes their stop from 12pts to 15pts hoping it fixes the
 * stop-outs, but never gets a clean before/after WR comparison because
 * trades from both bracket configurations are mixed in every aggregate.
 *
 * This analyzer attributes each trade to the specific template version that
 * was active at trade-entry time (via `findVersionForTrade` from
 * ntTemplateParser), groups by version, and surfaces the delta when:
 *   - both the current and the immediately-prior version have enough trades
 *     to be statistically meaningful, AND
 *   - the WR gap exceeds MIN_WR_GAP
 *
 * Severity:
 *   SUCCESS — newest version improved WR (bracket change paid off)
 *   WARNING — newest version regressed (worth investigating whether the
 *             change cost edge or the cohort is noisy)
 *
 * Reads:
 *   trade.atmStrategy, trade.netPnl, trade.date, trade.entryTime, trade.rMultiple
 *   ntTemplateVersions[atmName][] — version objects from useAtmManagement
 *
 * Why this is in the free tier:
 *   Version comparison is the single highest-value insight the version-history
 *   data enables. Locking it behind pro would defeat the point of building
 *   the version detection in the first place.
 *
 * ─── Voice rebalance (2026-05-27) ───────────────────────────────────────
 *
 * The 2026-05-26 in-the-wild output that triggered the rebalance read:
 *   "Your bracket adjustments are systematically destroying edge — roll
 *    back to your March 4th bracket parameters immediately."
 *
 * The data analysis was correct; the framing was "you failed." This file
 * was one of the contributors — the analyzer's own WARNING title used the
 * verdict phrase "version change cost edge" and the action said "Roll
 * back to the prior bracket params." The LLM dutifully amplified both.
 *
 * The fix here doesn't change the math; it changes the framing of the
 * observation the LLM weaves into prose. Three principles applied:
 *   1. Lead with what the data shows, not with a verdict. WR moved; we
 *      don't know yet whether the cause is the change, the regime, or
 *      the cohort being small.
 *   2. Surface options ("if you want a clean A/B…"), not commands.
 *   3. Trust the trader — they made the change for a reason. The action
 *      asks what they were optimizing for instead of assuming they were
 *      wrong to try.
 *
 * BEFORE (WARNING case)
 *   title:  "BE-then-trail version change cost edge — WR 80% → 20%"
 *   action: "Roll back to the prior bracket params or A/B-test the change
 *            on a smaller account. The new bracket structure is leaking
 *            60% of WR."
 *
 * AFTER (WARNING case)
 *   title:  "BE-then-trail — recent change may have cost edge: WR 80% → 20%
 *            (5 vs 5 trades)"
 *   action: "Two readings worth weighing here — the bracket change cost
 *            edge, or the new config is fine and the 5-trade cohort is
 *            noisy. Running the prior bracket on a small account for a
 *            week would A/B the change cleanly. Also worth asking what
 *            you were optimizing for when you made the change — if the
 *            goal was cutting stop-outs faster, the WR drop might be the
 *            cost you accepted."
 *
 * SUCCESS framing barely changes — celebrating a real win doesn't need
 * softening, just trimming the imperative tone in the action text.
 * ─────────────────────────────────────────────────────────────────────────
 */

import { CATEGORY, SEVERITY, makeObservation } from '../../types.js';
import { findVersionForTrade, diffAtmVersions, diffFieldLabel, matchExitsToBrackets } from '../../../ntTemplateParser.js';

export const key = 'atmVersionComparison';
export const tier = 'free';

// Need this many trades on EACH version being compared. Fewer than 4 and
// you're comparing noise — futures WR moves a lot in small samples.
const MIN_TRADES_PER_VERSION = 4;
// 15pp WR gap. Lower than ATM decay's 20pp because version changes are
// causal — the trader DELIBERATELY changed something, so we should be
// quicker to flag whether it worked.
const MIN_WR_GAP = 0.15;
// Per-bracket attribution (Phase 17): how much of the cross-version delta
// has to be explained by ONE bracket's R-capture change before we call it
// "the bracket doing the work." Bigger = more confident the bracket is
// responsible, not just along for the ride.
const MIN_BRACKET_ATTRIBUTION = 0.40;  // bracket explains ≥40% of total R delta

const fmtPct = (n) => `${Math.round(n * 100)}%`;
const fmtR = (n) => Number.isFinite(n) ? `${n >= 0 ? '+' : ''}${n.toFixed(2)}R` : '—';

/**
 * Render a structural-change list into a short, human-readable summary.
 * Picks the most-informative items first; bracket additions/removals,
 * then numeric param changes (SL/TP/Trail). Caps at maxItems with a
 * "+N more" suffix so the detail line stays under the 180-char limit.
 */
const summarizeChanges = (changes, maxItems = 3) => {
  if (!Array.isArray(changes) || changes.length === 0) return 'no parameter diff (label or other non-bracket change)';
  const labeled = changes.map(c => {
    const label = diffFieldLabel(c.field);
    const bPart = c.bracket != null ? `B${c.bracket + 1}` : '';
    const sPart = c.step != null ? `s${c.step + 1}` : '';
    if (c.field === 'bracketAdded') return `${bPart} added`;
    if (c.field === 'bracketRemoved') return `${bPart} removed`;
    if (c.field === 'trailStepAdded') return `${bPart} trail step ${sPart} added`;
    if (c.field === 'trailStepRemoved') return `${bPart} trail step ${sPart} removed`;
    if (c.field === 'stopStrategyAdded') return `${bPart} trail added`;
    if (c.field === 'stopStrategyRemoved') return `${bPart} trail removed`;
    const where = [bPart, sPart].filter(Boolean).join(' ');
    return `${where ? where + ' ' : ''}${label} ${c.old}→${c.new}`;
  });
  const head = labeled.slice(0, maxItems).join(', ');
  const more = labeled.length > maxItems ? `, +${labeled.length - maxItems} more` : '';
  return head + more;
};

/**
 * Pick a display label for a version. Prefers the user-set `label` if any,
 * otherwise builds a short timestamp-derived fallback so the detail line
 * always says SOMETHING distinguishing the two versions being compared.
 */
const versionLabel = (v, fallback) => {
  const explicit = (v?.label || '').trim();
  if (explicit) return explicit;
  if (v?.detectedAt) {
    try {
      const d = new Date(v.detectedAt);
      if (!Number.isNaN(d.getTime())) {
        return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      }
    } catch { /* fall through */ }
  }
  return fallback;
};

const computeStats = (trades) => {
  const wins = trades.filter(t => Number.isFinite(t.netPnl) && t.netPnl > 0);
  const wr = trades.length > 0 ? wins.length / trades.length : 0;
  const totalNet = trades.reduce((s, t) => s + (Number.isFinite(t.netPnl) ? t.netPnl : 0), 0);
  const rs = trades.map(t => parseFloat(t.rMultiple)).filter(Number.isFinite);
  const avgR = rs.length > 0 ? rs.reduce((s, r) => s + r, 0) / rs.length : null;
  return { count: trades.length, wins: wins.length, wr, totalNet, avgR };
};

/**
 * Per-bracket attribution (Phase 17).
 *
 * Given a set of trades and the snapshot for the version they belong to,
 * use matchExitsToBrackets to compute each bracket's contribution to total
 * R captured. Returns an array of { bracketIndex, totalR, count } so the
 * caller can compare per-bracket performance across two versions.
 *
 * Without an instrument spec (tickSize/tickValue/pointValue), bracket
 * matching can't compute R per exit — returns null gracefully. The atm
 * scope doesn't currently thread futuresSpecs down; we infer a fallback
 * by reading tradeProcessor-style fields (entry/exit price → R via the
 * version's stopLossTicks). When even that's not possible, returns null.
 */
const attributeByBracket = (trades, versionSnapshot, futuresSpecs) => {
  if (!versionSnapshot?.brackets || versionSnapshot.brackets.length === 0) return null;
  if (!Array.isArray(trades) || trades.length === 0) return null;

  const perBracket = new Map();  // bracketIndex → { totalR, count, wins }
  let attributable = 0;

  for (const t of trades) {
    if (!t.symbol || !t.exits || t.exits.length === 0) continue;
    // futuresSpecs is a lookup table by symbol (e.g., { MNQ: {tickSize, tickValue, pointValue}, ... })
    // Fall back to a minimal spec built from the version's stopLossTicks
    // + the trade's stopLoss + entry — best-effort but enough for bracket
    // matching by quantity (which is the primary axis anyway).
    const spec = futuresSpecs?.[t.symbol] ||
      (t.stopLossPoints && parseFloat(t.entry)
        ? { tickSize: 0.25, tickValue: 0.5, pointValue: 2 }  // MNQ-ish default
        : null);
    if (!spec) continue;
    const matches = matchExitsToBrackets(t, versionSnapshot, spec);
    if (!Array.isArray(matches) || matches.length === 0) continue;
    attributable++;
    for (const m of matches) {
      const idx = m.bracketIndex;
      if (!perBracket.has(idx)) perBracket.set(idx, { bracketIndex: idx, totalR: 0, count: 0, wins: 0 });
      const acc = perBracket.get(idx);
      acc.totalR += Number.isFinite(m.rCaptured) ? m.rCaptured : 0;
      acc.count += 1;
      if ((m.rCaptured || 0) > 0) acc.wins += 1;
    }
  }

  // If we couldn't attribute at least half the trades, the data is too
  // sparse to draw conclusions. Return null instead of misleading per-
  // bracket numbers.
  if (attributable < Math.max(2, Math.floor(trades.length / 2))) return null;

  return [...perBracket.values()].sort((a, b) => a.bracketIndex - b.bracketIndex);
};

/**
 * Given per-bracket stats for current vs prior versions + the list of
 * brackets that changed, identify which bracket explains the most of the
 * total R delta. Returns { bracketIndex, sharePct, contribution } when one
 * bracket explains ≥ MIN_BRACKET_ATTRIBUTION of the total. Otherwise null.
 */
const findDominantBracket = (priorPerBracket, currentPerBracket, changedBrackets) => {
  if (!priorPerBracket || !currentPerBracket) return null;
  if (!Array.isArray(changedBrackets) || changedBrackets.length === 0) return null;
  if (changedBrackets.length === 1) {
    // Trivial case — only one bracket changed, so it's automatically dominant
    return { bracketIndex: changedBrackets[0], sharePct: 1.0, contribution: null };
  }

  const priorByIdx = new Map(priorPerBracket.map(b => [b.bracketIndex, b]));
  const currentByIdx = new Map(currentPerBracket.map(b => [b.bracketIndex, b]));

  // For each changed bracket, compute avg R delta × count = total R contribution
  const contributions = [];
  for (const idx of changedBrackets) {
    const p = priorByIdx.get(idx);
    const c = currentByIdx.get(idx);
    if (!p || !c) continue;
    const pAvg = p.count > 0 ? p.totalR / p.count : 0;
    const cAvg = c.count > 0 ? c.totalR / c.count : 0;
    const contribution = (cAvg - pAvg) * Math.min(p.count, c.count);
    contributions.push({ bracketIndex: idx, contribution, pAvg, cAvg });
  }
  if (contributions.length === 0) return null;

  const totalAbs = contributions.reduce((s, c) => s + Math.abs(c.contribution), 0);
  if (totalAbs === 0) return null;

  contributions.sort((a, b) => Math.abs(b.contribution) - Math.abs(a.contribution));
  const top = contributions[0];
  const share = Math.abs(top.contribution) / totalAbs;
  if (share < MIN_BRACKET_ATTRIBUTION) return null;

  return { bracketIndex: top.bracketIndex, sharePct: share, contribution: top };
};

export const analyze = (ctx) => {
  const { atmName, atmTrades, ntTemplateVersions, futuresSpecs } = ctx || {};
  if (!atmName || !Array.isArray(atmTrades) || atmTrades.length === 0) return null;
  if (!ntTemplateVersions || typeof ntTemplateVersions !== 'object') return null;

  const versions = ntTemplateVersions[atmName];
  if (!Array.isArray(versions) || versions.length < 2) return null;

  // Live-trade gate: version comparison computes current vs prior WR
  // (computeStats), and the SUCCESS/WARNING + action text hinge on which
  // version is "winning." Open trades have unrealized P&L which isn't a
  // realized outcome — including them in either cohort would fabricate a
  // phantom "version change paid off" or "version change cost edge"
  // claim while the user is still in the trade. Filter at the top so the
  // bracket-attribution per-version trade lists also inherit the gate.
  // Matches summarizePnl semantics.
  const closedAtmTrades = atmTrades.filter(t => t?.isOpen !== true);
  if (closedAtmTrades.length === 0) return null;

  // Attribute each trade to its active version. findVersionForTrade is
  // newest-first aware; trades older than the oldest version fall back to
  // that oldest version (the only sensible default — those trades were
  // taken under SOME bracket structure, just one we never captured).
  const byVersion = new Map();
  for (const t of closedAtmTrades) {
    const v = findVersionForTrade(t, versions);
    if (!v?.versionId) continue;
    if (!byVersion.has(v.versionId)) byVersion.set(v.versionId, { version: v, trades: [] });
    byVersion.get(v.versionId).trades.push(t);
  }
  if (byVersion.size < 2) return null;

  // Build stats per version, drop versions that don't meet the sample-size
  // threshold so we don't compare noise to signal.
  const stats = new Map();
  for (const [vid, { version, trades }] of byVersion) {
    if (trades.length < MIN_TRADES_PER_VERSION) continue;
    stats.set(vid, { version, ...computeStats(trades) });
  }
  if (stats.size < 2) return null;

  // Versions are newest-first by construction (useAtmManagement prepends).
  // Walk forward from index 0 to find the newest version that survived
  // the sample-size filter, then walk further to find the next one. That's
  // our "current vs prior" comparison — the most recent change.
  let currentStat = null;
  let currentVersion = null;
  let priorStat = null;
  let priorVersion = null;
  for (const v of versions) {
    const s = stats.get(v.versionId);
    if (!s) continue;
    if (!currentStat) { currentStat = s; currentVersion = v; continue; }
    priorStat = s; priorVersion = v;
    break;
  }
  if (!currentStat || !priorStat) return null;

  const delta = currentStat.wr - priorStat.wr;
  if (Math.abs(delta) < MIN_WR_GAP) return null;

  const changes = diffAtmVersions(priorVersion, currentVersion);
  const changesText = summarizeChanges(changes);
  const cLabel = versionLabel(currentVersion, 'current');
  const pLabel = versionLabel(priorVersion, 'prior');
  const improved = delta > 0;

  // Task A.2 — templateType awareness.
  // When the management style itself changed across versions (TP→AT, AT→TP,
  // or to/from Hybrid), the comparison isn't a parameter tweak — it's a
  // different strategy. Trade cohorts on each side were operating under
  // different exit semantics: a TP version sells at fixed levels; an AT
  // version trails for runners. The WR delta is still real, but its
  // interpretation depends on knowing the change was categorical, not
  // numeric. Surface this in the headline so the LLM doesn't synthesize it
  // away into the same "trail-stopLoss tweak" framing the AT-to-AT case
  // would warrant. Snapshot can be a stub (line 427 in useAtmManagement) —
  // guard for missing templateType.
  const currentTemplateType = currentVersion?.snapshot?.templateType || null;
  const priorTemplateType = priorVersion?.snapshot?.templateType || null;
  const templateTypeChange = (
    currentTemplateType && priorTemplateType && currentTemplateType !== priorTemplateType
  ) ? { from: priorTemplateType, to: currentTemplateType } : null;
  const typeChangeText = templateTypeChange
    ? ` Management style switched ${templateTypeChange.from}→${templateTypeChange.to} — that's a different exit thesis, not just a parameter tweak; the WR comparison is between two cohorts trading under different mechanics.`
    : '';

  // Per-bracket attribution (Phase 17) — when multiple brackets changed,
  // figure out which one is actually responsible for the WR delta.
  // The list of changed brackets is derived from the diffs.
  const changedBrackets = [...new Set(
    changes.filter(c => typeof c.bracket === 'number').map(c => c.bracket)
  )];
  let dominantBracket = null;
  let bracketAttributionText = '';
  if (changedBrackets.length >= 2) {
    const priorTrades = byVersion.get(priorVersion.versionId)?.trades || [];
    const currentTrades = byVersion.get(currentVersion.versionId)?.trades || [];
    const priorPerBracket = attributeByBracket(priorTrades, priorVersion.snapshot, futuresSpecs);
    const currentPerBracket = attributeByBracket(currentTrades, currentVersion.snapshot, futuresSpecs);
    dominantBracket = findDominantBracket(priorPerBracket, currentPerBracket, changedBrackets);
    if (dominantBracket) {
      const bIdx = dominantBracket.bracketIndex;
      // Frame the attribution as where the R delta concentrates (an
      // observation), not as which bracket "is doing the work" (a causal
      // claim the per-trade attribution can't actually prove).
      bracketAttributionText = ` Most of the R delta concentrates on B${bIdx + 1} (${Math.round(dominantBracket.sharePct * 100)}% of the swing) — the other bracket changes look incidental in the data.`;
    }
  } else if (changedBrackets.length === 1) {
    // Single-bracket change — attribution is trivial but worth surfacing
    dominantBracket = { bracketIndex: changedBrackets[0], sharePct: 1.0, contribution: null };
  }

  return makeObservation({
    id: `${key}:${atmName}:${currentVersion.versionId}-vs-${priorVersion.versionId}`,
    analyzer: key,
    scope: 'atm',
    scopeId: atmName,
    severity: improved ? SEVERITY.SUCCESS : SEVERITY.WARNING,
    category: CATEGORY.SETUP,
    // Title framing notes (2026-05-27 rebalance):
    //   SUCCESS — "paid off" stays. It's a confident description of a
    //     positive outcome, which is honest framing for a SUCCESS observation
    //     and doesn't need softening.
    //   WARNING — softened from "version change cost edge" (verdict) to
    //     "recent change may have cost edge" (hypothesis). The literal
    //     phrase "cost edge" is preserved so atmVersionComparison.test.mjs
    //     keeps passing — the test probes severity-by-keyword, not the
    //     full title text. Adding the sample-size in parens up front is
    //     also a load-bearing change: it makes the LLM less likely to
    //     amplify the headline number without the caveat.
    title: improved
      ? (templateTypeChange
          ? `"${atmName}" switched ${templateTypeChange.from}→${templateTypeChange.to} and WR climbed ${fmtPct(priorStat.wr)} → ${fmtPct(currentStat.wr)}`
          : `"${atmName}" version change paid off — WR ${fmtPct(priorStat.wr)} → ${fmtPct(currentStat.wr)}`)
      : (templateTypeChange
          ? `"${atmName}" switched ${templateTypeChange.from}→${templateTypeChange.to} — WR ${fmtPct(priorStat.wr)} → ${fmtPct(currentStat.wr)} (${priorStat.count} vs ${currentStat.count} trades, different exit mechanics)`
          : `"${atmName}" — recent change may have cost edge: WR ${fmtPct(priorStat.wr)} → ${fmtPct(currentStat.wr)} (${priorStat.count} vs ${currentStat.count} trades)`),
    detail: `${cLabel} (current): ${currentStat.wins}/${currentStat.count} winners (${fmtPct(currentStat.wr)})${currentStat.avgR != null ? `, avg ${fmtR(currentStat.avgR)}` : ''}. ${pLabel} (prior): ${priorStat.wins}/${priorStat.count} winners (${fmtPct(priorStat.wr)})${priorStat.avgR != null ? `, avg ${fmtR(priorStat.avgR)}` : ''}. Changes: ${changesText}.${typeChangeText}${bracketAttributionText}`,
    // Action framing notes (2026-05-27 rebalance):
    //   SUCCESS actions stay imperative-ish ("Lock this version in") because
    //     consolidating a confirmed win is a different speech-act than
    //     correcting an unproven loss. The light imperative reads as
    //     encouragement, not scolding.
    //   WARNING actions are reframed as OPTIONS the trader can take, not
    //     orders. The "Roll back immediately" pattern from the in-the-wild
    //     2026-05-26 example originated here and is what the rebalance is
    //     correcting. The new framing offers two readings (real edge cost
    //     vs. noisy cohort), names the A/B that would disambiguate, and
    //     asks what the trader was optimizing for — trust over assumption.
    action: improved
      ? (dominantBracket && dominantBracket.sharePct >= MIN_BRACKET_ATTRIBUTION && changedBrackets.length >= 2
          ? `Lock this version in. The data so far suggests B${dominantBracket.bracketIndex + 1} is the lever doing most of the work — if you iterate further, focusing on B${dominantBracket.bracketIndex + 1} and holding the others would extend the experiment cleanly.`
          : `Lock this version in — label it in the ATM card's Version History so the next iteration doesn't accidentally walk back the win.`)
      : (dominantBracket && dominantBracket.sharePct >= MIN_BRACKET_ATTRIBUTION && changedBrackets.length >= 2
          ? `If you want to isolate the cause, B${dominantBracket.bracketIndex + 1} is the bracket the data implicates most — holding the other changes and reverting just B${dominantBracket.bracketIndex + 1} would tell you whether that one knob is responsible. Worth asking what you were tuning for when you adjusted it.`
          : `Two readings worth weighing — the change cost edge (${fmtPct(Math.abs(delta))} WR), or the new config is fine and the ${currentStat.count}-trade cohort is noisy. Running the prior bracket on a small account for a week would A/B the change. Also worth surfacing what you were optimizing for when you made the change — the WR drop might be a cost you accepted for a different gain (faster stops, tighter risk).`),
    evidence: {
      currentVersionId: currentVersion.versionId,
      priorVersionId: priorVersion.versionId,
      currentWR: currentStat.wr,
      priorWR: priorStat.wr,
      delta,
      currentCount: currentStat.count,
      priorCount: priorStat.count,
      changes,
      changedBrackets,
      dominantBracket,  // null when attribution isn't possible / not dominant
      currentTemplateType,
      priorTemplateType,
      templateTypeChange,  // null when same type across versions
    },
    confidence: 0.82,
    // Regressions outrank improvements — a leak that's actively bleeding
    // money is more actionable than a confirmed win that's already paying.
    priority: improved ? 78 : 86,
  });
};

// Exports for tests
export {
  summarizeChanges,
  versionLabel,
  computeStats,
  attributeByBracket,
  findDominantBracket,
  MIN_TRADES_PER_VERSION,
  MIN_WR_GAP,
  MIN_BRACKET_ATTRIBUTION,
};
